import {
  AfriexCountryCode,
  AfriexCurrencyCode,
  AfriexTransactionChannel,
  AfriexTransactionType,
} from '@/types';
import type {BusinessAllowedFeature} from '@/types/AfriexBusiness';
import {BusinessFeatureType} from '@/types/AfriexBusiness';

const currencyOptions = Object.values(AfriexCurrencyCode)?.sort();
const countryOptions = Object.values(AfriexCountryCode)?.sort();
const channelOptions = Object.values(AfriexTransactionChannel);
const channelChildren = (parentKey: string, shouldExcludeChildren = false) => [
  {
    type: BusinessFeatureType.FromChannel,
    label: 'Source Channel',
    key: `${parentKey}.${BusinessFeatureType.FromChannel}`,
    children: shouldExcludeChildren
      ? null
      : channelOptions?.map((x) => ({
          type: BusinessFeatureType.FromChannel,
          label: x,
          value: x,
          key: `${parentKey}.${BusinessFeatureType.FromChannel}.${x}`,
        })),
  },
  {
    type: BusinessFeatureType.ToChannel,
    label: 'Destination Channel',
    key: `${parentKey}.${BusinessFeatureType.ToChannel}`,
    children: shouldExcludeChildren
      ? null
      : channelOptions?.map((x) => ({
          type: BusinessFeatureType.ToChannel,
          label: x,
          value: x,
          key: `${parentKey}.${BusinessFeatureType.ToChannel}.${x}`,
        })),
  },
];
const nonSwapTransactionChildren = (transactionType: AfriexTransactionType) =>
  transactionType !== AfriexTransactionType.SWAP
    ? [
        {
          type: BusinessFeatureType.FromCountry,
          label: 'Source Country',
          key: `${transactionType}.${BusinessFeatureType.FromCountry}`,
          children: countryOptions.map((x) => ({
            type: BusinessFeatureType.FromCountry,
            label: x,
            value: x,
            key: `${transactionType}.${BusinessFeatureType.FromCountry}.${x}`,
            children: channelChildren(
              `${transactionType}.${BusinessFeatureType.FromCountry}.${x}`,
            ),
          })),
        },
        {
          type: BusinessFeatureType.ToCountry,
          label: 'Destination Country',
          key: `${transactionType}${BusinessFeatureType.ToCountry}`,
          children: countryOptions.map((x) => ({
            type: BusinessFeatureType.ToCountry,
            label: x,
            value: x,
            key: `${transactionType}${BusinessFeatureType.ToCountry}${x}`,
            children: channelChildren(
              `${transactionType}${BusinessFeatureType.ToCountry}${x}`,
            ),
          })),
        },
      ]
    : [];
const commonTransactionChildren = (transactionType: AfriexTransactionType) => [
  {
    type: BusinessFeatureType.FromCurrency,
    label: 'Source Currency',
    key: `${transactionType}.${BusinessFeatureType.FromCurrency}`,
    children: currencyOptions?.map((x) => ({
      type: BusinessFeatureType.FromCurrency,
      label: x,
      value: x,
      key: `${transactionType}.${BusinessFeatureType.FromCurrency}.${x}`,
      children: channelChildren(
        `${transactionType}.${BusinessFeatureType.FromCurrency}.${x}`,
        transactionType === AfriexTransactionType.SWAP,
      ),
    })),
  },
  {
    type: BusinessFeatureType.ToCurrency,
    label: 'Destination Currency',
    key: `${transactionType}.${BusinessFeatureType.ToCurrency}`,
    children: currencyOptions.map((x) => ({
      type: BusinessFeatureType.ToCurrency,
      label: x,
      value: x,
      key: `${transactionType}.${BusinessFeatureType.ToCurrency}.${x}`,
      children: channelChildren(
        `${transactionType}.${BusinessFeatureType.ToCurrency}.${x}`,
        transactionType === AfriexTransactionType.SWAP,
      ),
    })),
  },
  ...nonSwapTransactionChildren(transactionType),
];

const allowedFeatureOptions: BusinessAllowedFeature[] = [
  {
    type: BusinessFeatureType.TransactionType,
    label: 'Deposit Transaction',
    value: AfriexTransactionType.DEPOSIT,
    key: AfriexTransactionType.DEPOSIT,
    children: commonTransactionChildren(AfriexTransactionType.DEPOSIT),
  },
  {
    type: BusinessFeatureType.TransactionType,
    label: 'Withdrawal Transaction',
    value: AfriexTransactionType.WITHDRAW,
    key: AfriexTransactionType.WITHDRAW,
    children: commonTransactionChildren(AfriexTransactionType.WITHDRAW),
  },
  {
    type: BusinessFeatureType.TransactionType,
    label: 'Swap Transaction',
    value: AfriexTransactionType.SWAP,
    key: AfriexTransactionType.SWAP,
    children: commonTransactionChildren(AfriexTransactionType.SWAP),
  },
];
export const defaultFields: BusinessAllowedFeature[] = allowedFeatureOptions;
